import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Sparkles, ArrowRight, GraduationCap, Users, Trophy } from 'lucide-react';
import lightBackgroundVideo from '../assets/Video/background-video-light.mp4';
import darkBackgroundVideo from '../assets/Video/background-video-dark.mp4';
import { useTheme } from '../hooks/useTheme';
import { useEffect, useRef, useState } from 'react';
// Analytics tracking removed

export default function Hero() {
  const { theme } = useTheme();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoKey, setVideoKey] = useState(0);

  // Force video reload when theme changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      setVideoKey(prev => prev + 1); // Force remount
    }
  }, [theme]);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="home" className="min-h-screen flex items-center justify-center py-20 relative overflow-hidden">
      {/* Background video */}
      <div className="absolute inset-0 z-0">
        <video
          ref={videoRef}
          key={videoKey} // Use videoKey instead of theme to force remount
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover opacity-90"
        >
          {theme === 'dark' ? (
            <source src={darkBackgroundVideo} type="video/mp4" />
          ) : (
            <>
              <source src={lightBackgroundVideo} type="video/quicktime" />
              <source src={lightBackgroundVideo} type="video/mp4" />
            </>
          )}
        </video>
        <div className="absolute inset-0 bg-background/10 backdrop-blur-[1px]"></div>
      </div>
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="mb-6"
            >
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 dark:bg-gray-800/50 backdrop-blur-sm border border-white/20 dark:border-gray-700">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-foreground">
                  Nové kurzy na nový rok 2026 čoskoro
                </span>
              </div>
            </motion.div>
            <div className="overflow-hidden">
              <motion.h1 
                className="text-5xl md:text-7xl font-bold text-foreground mb-6 font-sans tracking-tight [text-shadow:_0_1px_3px_rgba(0,0,0,0.3)]"
                initial={{ y: 100 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
              >
                <motion.span 
                  className="inline-block"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  Naučte sa používať AI
                </motion.span>{" "}
                <motion.span 
                  className="inline-block text-primary"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  s expertmi z praxe
                </motion.span>
              </motion.h1>
            </div>
            <motion.p 
              className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto [text-shadow:_0_1px_2px_rgba(0,0,0,0.2)]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              Praktické AI zručnosti pre každodenný život i profesionálnu prax.
              Kurzy pre všetkých bez ohľadu na predchádzajúce skúsenosti.
            </motion.p>
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.8 }}
            >
              <Button 
                size="lg" 
                className="group"
                onClick={() => {
                  // Analytics tracking removed
                  scrollToSection('schedule');
                }}
              >
                Začať s kurzom
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => {
                  // Analytics tracking removed
                  scrollToSection('instructor');
                }}
              >
                Zistiť viac o lektoroch
              </Button>
            </motion.div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16"
          >
            {[
              {
                title: 'Varianty',
                description: 'Kurzov vytvorených na základe reálnych potrieb trhu',
                icon: GraduationCap
              },
              {
                title: 'Stovky',
                description: 'Spokojných účastníkov z rôznych odvetví a oblastí',
                icon: Users
              },
              {
                title: 'Vysoká',
                description: 'Úspešnosť absolventov pri uplatnení získaných AI zručností',
                icon: Trophy
              },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.2 + (index * 0.1) }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <div className="text-center p-6 rounded-lg bg-white/10 dark:bg-gray-800/50 backdrop-blur-sm border border-white/20 dark:border-gray-700">
                  <stat.icon className="w-6 h-6 text-primary mb-4 mx-auto" />
                  <h3 className="text-3xl font-bold text-foreground mb-2">
                    {stat.title}
                  </h3>
                  <p className="text-muted-foreground">
                    {stat.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}